import React from "react";
import {
  Close,
  Lightbulb,
  AutoAwesomeMosaic,
} from "@mui/icons-material";
import {
  Stack,
  Button,
  Box,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { RightPanel } from "../../../../components/RightPanel";
import { PreviewTemplate } from "../../../../components/Preview/PreviewTemplate";
import { MarketCampaignStatusRenderer } from "../../../../components/StatusRenderer/MarketCampaignStatusRenderer";
import WhatsappBgImg from "../../../../assets/whatsapp_bg_img.jpg";
import { formatText } from "../../../../helpers";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { IPaymentReminderLog } from "../types/IPaymentReminderLog";
import { IWhatsAppTemplateData } from "../../../../redux/whatsappTemplate/whatsappTemplate.types";

interface IViewPaymentReminderLogDialogProps {
  open: boolean;
  data: IPaymentReminderLog;
  onClose: () => void;
}

/** Single-column label/value row (replaces the 2-column GridItem layout) */
const DetailRow: React.FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    sx={{ borderBottom: "1px solid #f0f0f0", py: 1, px: 0.5 }}
  >
    <Typography color="textSecondary" variant="h6" fontWeight="700">
      {label}
    </Typography>
    <Typography color="textSecondary" variant="h6" fontWeight="400">
      {!value || value.toString().length === 0 ? "--" : value}
    </Typography>
  </Box>
);

/**
 * Builds a PreviewTemplate-compatible template object from the log's
 * `content` array (which mirrors WhatsApp template components: HEADER/BODY/FOOTER/BUTTONS).
 */
const buildTemplateFromLog = (log: IPaymentReminderLog): IWhatsAppTemplateData => {
  const content = log.content || [];
  const headerComp = content.find((c) => c.type === "HEADER");
  const footerComp = content.find((c) => c.type === "FOOTER");
  const buttonsComp = content.find((c) => c.type === "BUTTONS");
  // Body content often has no explicit "type" — it's the item with plain "text" and no type
  const bodyComp = content.find((c) => c.type === "BODY") || content.find((c) => !c.type && c.text);

  return {
    name: log.template_name || "",
    category: "",
    example: { header_text: [], body_text: [] },
    components: [],
    header: {
      type: "HEADER",
      format: (headerComp?.format as any) || null,
      text: headerComp?.text || null,
      example: {
        header_text: [[]],
        header_handle: headerComp?.example?.header_handle
          ? (headerComp.example.header_handle as any)
          : null,
      },
    },
    body: {
      type: "BODY",
      text: bodyComp?.text || "",
      example: { body_text: [[]] },
    },
    footer: {
      type: "FOOTER",
      text: footerComp?.text || "",
    },
    buttons: {
      type: "BUTTONS",
      buttons: buttonsComp?.buttons || [],
    },
    company_outlet_uuid: log.company_outlet_uuid || "",
    meta_id: null,
  } as IWhatsAppTemplateData;
};

/**
 * ViewPaymentReminderLogDialog
 *
 * Right panel showing full details of a sent payment reminder log
 * (recipient info, event/venue info, template + live preview, delivery timeline).
 * Pattern reference: ViewMarketingCampaignRightPanel.
 */
export const ViewPaymentReminderLogDialog: React.FC<
  IViewPaymentReminderLogDialogProps
> = ({ open, data, onClose }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const { ftDate } = useTimezoneFormatter();

  const template = buildTemplateFromLog(data);
  const headerMediaUrl = data.content?.find((c) => c.type === "HEADER")
    ?.example?.header_handle?.[0];

  return (
    <RightPanel
      heading={formatText(data?.name_of_guest || data?.recipient_name || "Reminder") + " — Log Details"}
      open={open}
      width="70%"
      onClose={onClose}
      actionButtons={() => (
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={onClose}>
              <Close sx={{ fontSize: 18, mr: 0.5 }} />
              Close
            </Button>
          </Stack>
        </Stack>
      )}
    >
      <Grid
        container
        spacing={1}
        sx={{ flexDirection: isDesktop ? "row" : "column" }}
      >
        <Grid item xs={12} md={7.8}>
          {/* Basic Info */}
          <Card sx={{ mb: 1.5, p: 0 }}>
            <CardHeader
              title={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Lightbulb sx={{ fontSize: 20, color: "primary.main" }} />
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                      Guest Information
                    </Typography>
                  </Box>
                  <MarketCampaignStatusRenderer status={data.status || ""} />
                </Box>
              }
              titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <DetailRow label="Guest Name" value={data.name_of_guest || data.recipient_name} />
              <DetailRow label="Phone Number" value={data.recipient_number || data.phone || data.to_number} />
              <DetailRow label="Venue" value={data.preferred_venue} />
              <DetailRow
                label="Event Date"
                value={data.function_date ? ftDate(data.function_date) : null}
              />
              <DetailRow label="Event Time" value={data.event_time} />

            </CardContent>
          </Card>

          {/* Reminder Information */}
          <Card sx={{ p: 0 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <AutoAwesomeMosaic sx={{ fontSize: 20, color: "primary.main" }} />
                  <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    Reminder Information
                  </Typography>
                </Box>
              }
              titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <DetailRow label="Reminder Name" value={data.campaign_name} />
              <DetailRow label="Template Name" value={data.template_name} />
              <DetailRow label="Content Type" value={data.content_type} />
              <DetailRow label="Platform" value={data.platform} />
              {data.error_message && (
                <DetailRow label="Error" value={data.error_message} />
              )}
            </CardContent>
          </Card>

          {/* Mobile Preview — below content on small screens */}
          {!isDesktop && (
            <Card sx={{ mt: 1.5, p: 0 }}>
              <CardHeader
                title={
                  <Typography variant="subtitle1" fontWeight={600}>
                    Message Preview
                  </Typography>
                }
                titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box
                  sx={{
                    backgroundColor: "#efeae2",
                    backgroundImage: `url(${WhatsappBgImg})`,
                    backgroundRepeat: "repeat",
                    backgroundSize: "auto",
                    backgroundBlendMode: "multiply",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <PreviewTemplate
                    bgImage={false}
                    template={template}
                    headerType={template.header?.format || ""}
                    headerVariableValue={
                      typeof headerMediaUrl === "string" ? headerMediaUrl : undefined
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Preview — Desktop only */}
        {isDesktop && (
          <Grid
            item
            xs={12}
            md={4.2}
            sx={{
              backgroundColor: "#efeae2",
              backgroundImage: `url(${WhatsappBgImg})`,
              backgroundRepeat: "repeat",
              backgroundSize: "auto",
              backgroundBlendMode: "multiply",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              overflowX: "hidden",
              pt: 1,
              pb: 1,
              mt: 1.5,
              position: "sticky",
              top: 0,
              alignSelf: "flex-start",
            }}
          >
            <PreviewTemplate
              bgImage={false}
              template={template}
              headerType={template.header?.format || ""}
              headerVariableValue={
                typeof headerMediaUrl === "string" ? headerMediaUrl : undefined
              }
            />
          </Grid>
        )}
      </Grid>
    </RightPanel>
  );
};
